import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { FounderBadge, PricingTierBadge } from "../components/FounderBadge";
import {
  TrendingUp,
  Home,
  Hammer,
  DollarSign,
  Shield,
  Check,
  ArrowRight,
  Loader2,
  Sparkles
} from "lucide-react";
import DealForgeLogo from "../components/DealForgeLogo";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Portal icons and colors
const PORTAL_CONFIG = {
  investor: { icon: TrendingUp, color: "#D4AF37", name: "Investor Portal" },
  realtor: { icon: Home, color: "#D4AF37", name: "Realtor Portal" },
  contractor: { icon: Hammer, color: "#3B82F6", name: "Contractor Portal" },
  lender: { icon: DollarSign, color: "#10B981", name: "Lender Portal" },
  title: { icon: Shield, color: "#10B981", name: "Title Portal" }
};

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Portal Card Component
function PortalCard({ portal, isSelected, isPrimary, onSelect, pricingTier }) {
  const config = PORTAL_CONFIG[portal.id];
  const Icon = config?.icon || Shield;
  const color = config?.color || "#D4AF37";
  
  // Calculate price based on tier
  const price = pricingTier === "beta_founder" 
    ? 99 
    : (isPrimary 
        ? { investor: 149, realtor: 149, contractor: 129, lender: 199, title: 199 }[portal.id] || 149
        : 129);

  return (
    <div 
      className={`relative p-5 border rounded-xl cursor-pointer transition-all duration-300 ${
        isSelected 
          ? "border-2 scale-[1.02] shadow-lg" 
          : "border-white/[0.08] hover:border-white/20 hover:scale-[1.01]"
      } bg-[#0A0A0A]/80 backdrop-blur-sm`}
      style={isSelected ? { borderColor: color, boxShadow: `0 0 30px ${color}20` } : {}}
      onClick={() => onSelect(portal.id)}
    >
      {isPrimary && isSelected && (
        <div 
          className="absolute -top-3 left-4 px-3 py-1 rounded-full text-[10px] font-bold uppercase"
          style={{ backgroundColor: color, color: '#000' }}
        >
          Primary Portal
        </div>
      )}
      
      <div className="flex items-start gap-4">
        <div 
          className="w-14 h-14 rounded-xl flex items-center justify-center transition-all"
          style={{ 
            backgroundColor: isSelected ? `${color}20` : `${color}10`, 
            border: `1px solid ${isSelected ? color : `${color}30`}` 
          }}
        >
          <Icon className="w-7 h-7" style={{ color }} />
        </div>
        
        <div className="flex-1">
          <h3 className="font-semibold text-white text-lg">{portal.name}</h3>
          <p className="text-white/50 text-sm mt-1 line-clamp-2">{portal.description}</p>
        </div>

        {isSelected && (
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: color }}
          >
            <Check className="w-5 h-5 text-black" />
          </div>
        )}
      </div>
      
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/[0.06]">
        <span className="text-white/40 text-sm">
          {isPrimary ? "Primary" : "Add-on"} Portal
        </span>
        <span className="font-mono font-bold text-lg" style={{ color }}>
          {formatCurrency(price)}<span className="text-white/40 text-sm font-normal">/mo</span>
        </span>
      </div>
    </div>
  );
}

export default function OnboardingPage({ user }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Select Primary, 2: Select Addons, 3: Confirm
  const [portals, setPortals] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [selectedPrimary, setSelectedPrimary] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const pricingTier = user?.pricing_tier || "standard";
  const isFounder = pricingTier === "beta_founder";

  useEffect(() => {
    // Check subscription status from API to determine if onboarding is needed
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/subscription/status`, { withCredentials: true });
        if (res.data.primary_portal) {
          // User already has a portal, redirect to dashboard
          navigate("/dashboard");
          return;
        }
        // Continue to show onboarding
        fetchPricing();
      } catch (err) {
        // If not authenticated, redirect to login
        navigate("/login");
      }
    };
    checkStatus();
  }, [navigate]);

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/subscription/pricing`);
      setPortals(res.data.portals || []);
      setPricing(res.data);
    } catch (err) {
      toast.error("Failed to load pricing information");
    } finally {
      setLoading(false);
    }
  };

  const handlePrimarySelect = (portalId) => {
    setSelectedPrimary(portalId);
    setSelectedAddons(prev => prev.filter(id => id !== portalId));
  };

  const handleAddonToggle = (portalId) => {
    if (portalId === selectedPrimary) return;
    setSelectedAddons(prev => 
      prev.includes(portalId)
        ? prev.filter(id => id !== portalId)
        : [...prev, portalId]
    );
  };

  const calculateTotal = () => {
    if (!selectedPrimary) return 0;
    
    if (isFounder) {
      return 99 + (selectedAddons.length * 99);
    }
    
    const primaryPrices = { investor: 149, realtor: 149, contractor: 129, lender: 199, title: 199 };
    return (primaryPrices[selectedPrimary] || 149) + (selectedAddons.length * 129);
  };

  const handleSubmit = async () => {
    if (!selectedPrimary) {
      toast.error("Please select a primary portal");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/subscription/select-portals`,
        {
          primary_portal: selectedPrimary,
          additional_portals: selectedAddons
        },
        { withCredentials: true }
      );
      
      toast.success("Portal selection saved! Welcome to DealForge.");
      
      // Navigate to the appropriate dashboard based on primary portal
      const portalRoutes = {
        investor: "/dashboard",
        realtor: "/realtor/dashboard",
        contractor: "/contractor/dashboard",
        lender: "/lender/dashboard",
        title: "/title/dashboard"
      };
      
      navigate(portalRoutes[selectedPrimary] || "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save portal selection");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#00FF88]/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <DealForgeLogo size="2xl" />
          </div>
          
          {isFounder && (
            <div className="flex justify-center mb-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full">
                <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-[#D4AF37] font-semibold">Founder Pricing Unlocked</span>
              </div>
            </div>
          )}
          
          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            Welcome to <span className="text-[#D4AF37]">DealForge</span>
          </h1>
          <p className="text-white/60 text-lg max-w-xl mx-auto">
            Select your primary portal to access the DealForge transaction network.
            {isFounder && " As a founder, you get locked-in pricing at $99/portal."}
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-4 mb-10">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  step >= s 
                    ? "bg-[#D4AF37] text-black" 
                    : "bg-white/10 text-white/40"
                }`}
              >
                {s}
              </div>
              {s < 3 && <div className={`w-12 h-0.5 ${step > s ? "bg-[#D4AF37]" : "bg-white/10"}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Select Primary Portal */}
        {step === 1 && (
          <div>
            <h2 className="text-xl font-semibold text-center mb-6">
              Select Your <span className="text-[#D4AF37]">Primary Portal</span>
            </h2>
            <p className="text-white/50 text-center mb-8">
              Your primary portal determines your main role in the DealForge network.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {portals.map(portal => (
                <PortalCard
                  key={portal.id}
                  portal={portal}
                  isSelected={selectedPrimary === portal.id}
                  isPrimary={true}
                  onSelect={handlePrimarySelect}
                  pricingTier={pricingTier}
                />
              ))}
            </div>

            <div className="flex justify-center">
              <Button
                onClick={() => setStep(2)}
                disabled={!selectedPrimary}
                className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold px-8 py-6 text-lg disabled:opacity-50"
              >
                Continue <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Select Additional Portals */}
        {step === 2 && (
          <div>
            <h2 className="text-xl font-semibold text-center mb-6">
              Add More <span className="text-[#D4AF37]">Portals</span> <span className="text-white/40 font-normal">(Optional)</span>
            </h2>
            <p className="text-white/50 text-center mb-8">
              Expand your access to other areas of the DealForge network.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {portals.filter(p => p.id !== selectedPrimary).map(portal => (
                <PortalCard
                  key={portal.id}
                  portal={portal}
                  isSelected={selectedAddons.includes(portal.id)}
                  isPrimary={false}
                  onSelect={handleAddonToggle}
                  pricingTier={pricingTier}
                />
              ))}
            </div>

            <div className="flex justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="border-white/20 text-white hover:bg-white/[0.04] px-6 py-6"
              >
                Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold px-8 py-6 text-lg"
              >
                {selectedAddons.length > 0 ? "Review Selection" : "Skip & Continue"} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <div>
            <h2 className="text-xl font-semibold text-center mb-6">
              Confirm Your <span className="text-[#D4AF37]">Selection</span>
            </h2>

            <div className="max-w-md mx-auto">
              <div className="bg-[#0A0A0A]/80 border border-white/[0.08] rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white/50">Your Plan</span>
                  <PricingTierBadge tier={pricingTier} />
                </div>

                {/* Primary Portal */}
                <div className="flex items-center justify-between py-3 border-b border-white/[0.06]">
                  <div className="flex items-center gap-3">
                    {(() => {
                      const config = PORTAL_CONFIG[selectedPrimary];
                      const Icon = config?.icon || Shield;
                      return (
                        <>
                          <Icon className="w-5 h-5" style={{ color: config?.color }} />
                          <div>
                            <span className="text-white capitalize">{selectedPrimary} Portal</span>
                            <span className="ml-2 px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] text-[9px] font-semibold uppercase rounded">
                              Primary
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <span className="text-white font-mono">
                    {formatCurrency(isFounder ? 99 : { investor: 149, realtor: 149, contractor: 129, lender: 199, title: 199 }[selectedPrimary])}
                  </span>
                </div>

                {/* Additional Portals */}
                {selectedAddons.map(portalId => {
                  const config = PORTAL_CONFIG[portalId];
                  const Icon = config?.icon || Shield;
                  return (
                    <div key={portalId} className="flex items-center justify-between py-3 border-b border-white/[0.06]">
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5" style={{ color: config?.color }} />
                        <span className="text-white capitalize">{portalId} Portal</span>
                      </div>
                      <span className="text-white font-mono">
                        {formatCurrency(isFounder ? 99 : 129)}
                      </span>
                    </div>
                  );
                })}

                {/* Total */}
                <div className="flex items-center justify-between pt-4 mt-2">
                  <span className="text-white font-semibold">Monthly Total</span>
                  <span className="text-[#D4AF37] font-mono font-bold text-2xl">
                    {formatCurrency(calculateTotal())}<span className="text-white/40 text-sm font-normal">/mo</span>
                  </span>
                </div>
              </div>

              {isFounder && (
                <div className="bg-[#D4AF37]/[0.05] border border-[#D4AF37]/20 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#D4AF37] font-semibold text-sm">Founder Pricing Locked</p>
                      <p className="text-white/60 text-sm mt-1">
                        Your $99/portal pricing is locked forever as long as you keep your subscription active.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3">
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold py-6 text-lg"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Complete Setup <ArrowRight className="ml-2 w-5 h-5" />
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  disabled={submitting}
                  className="w-full border-white/20 text-white hover:bg-white/[0.04] py-6"
                >
                  Back
                </Button>
              </div>

              <p className="text-center text-white/30 text-xs mt-4">
                Payment setup will be completed after portal selection.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
